/**
 * JSON Format Handler - Native JSON export/import for full fidelity data operations
 *
 * This handler provides the primary format for data export/import, maintaining
 * complete data fidelity including relationships, metadata, and complex structures.
 *
 * Key Features:
 * - Full data structure preservation
 * - Relationship mapping
 * - Metadata inclusion
 * - Schema validation
 * - Compression support
 * - Incremental update capabilities
 */

class JsonFormatHandler {
  constructor() {
    this.formatVersion = '1.0.0';
    this.compressionEnabled = true;
  }

  /**
   * Process data for JSON export
   * @param {Object} data - Aggregated trip data from all packages
   * @param {Object} options - Export options (compression, pretty print, etc.)
   * @returns {Object} Processed export result
   */
  async process(data, options = {}) {
    try {
      // Structure data in standard JSON format
      const jsonData = this.structureJsonData(data, options);

      // Apply formatting options
      const formattedData = this.applyFormatting(jsonData, options);

      // Compress if requested
      const finalData = options.compress !== false
        ? await this.compressData(formattedData)
        : formattedData;

      return {
        format: 'json',
        version: this.formatVersion,
        content: finalData, // Use 'content' instead of 'data' for browser download
        size: this.calculateSize(finalData),
        compressed: options.compress !== false,
        filename: this.generateFilename({ ...data.metadata, tripName: data.trip?.name }, 'json'),
        mimeType: 'application/json'
      };

    } catch (error) {
      throw new Error(`JSON export failed: ${error.message}`);
    }
  }

  /**
   * Structure raw data into standard JSON format
   * @param {Object} rawData - Raw aggregated data
   * @param {Object} options - Structuring options
   * @returns {Object} Structured JSON data
   */
  structureJsonData(rawData, options) {
    const jsonStructure = {
      // Metadata section
      export: {
        version: this.formatVersion,
        createdAt: new Date().toISOString(),
        exportedBy: 'Trip Planner JSON Handler',
        userId: rawData.metadata.userId,
        tripId: rawData.metadata.tripId,
        tripName: rawData.trip?.name || 'Unknown Trip',
        scope: rawData.metadata.scope,
        generator: rawData.metadata.generator || 'Trip Planner Export System'
      },

      // Trip information
      trip: rawData.trip || null,

      // Entity data organized by type
      entities: {
        days: this.processEntityData(rawData.days || [], 'day', options),
        places: this.processEntityData(rawData.places || [], 'place', options),
        accommodation: this.processEntityData(rawData.accommodation || [], 'accommodation', options),
        travel: this.processEntityData(rawData.travel || [], 'travel', options),
        documents: this.processEntityData(rawData.documents || [], 'document', options),
        extras: this.processEntityData(rawData.extras || [], 'extra', options)
      },

      // Relationships between entities
      relationships: rawData.relationships || this.extractRelationships(rawData),

      // Connection registry links + config (item-to-item relationships) — top-level,
      // matching the ZIP export's links.json / link-registry-config.json shape, so
      // importService can round-trip them without a separate unwrap step.
      links: rawData.links || [],
      _meta: rawData._meta,

      // User preferences and settings
      settings: rawData.settings || {},

      // Content blocks and custom data
      contentBlocks: rawData.contentBlocks || [],

      // Schema information for validation
      schema: {
        version: '1.0.0',
        entities: this.getEntitySchemas()
      },

      // Export statistics
      statistics: {
        entityCounts: this.calculateEntityCounts(rawData),
        exportSize: this.calculateSize(rawData),
        totalEntities: this.getTotalEntityCount(rawData)
      }
    };

    // Include additional metadata if requested
    if (options.includeMetadata) {
      jsonStructure.metadata = this.extractMetadata(rawData);
    }

    return jsonStructure;
  }

  /**
   * Process entity data with type-specific handling
   * @param {Array} entities - Array of entities
   * @param {string} entityType - Type of entities being processed
   * @param {Object} options - Processing options
   * @returns {Array} Processed entities
   */
  processEntityData(entities, entityType, options) {
    return entities.map(entity => {
      const processedEntity = {
        id: entity.id,
        type: entityType,
        ...entity
      };

      // Add type-specific processing
      switch (entityType) {
        case 'day':
          processedEntity.contentBlocks = entity.contentBlocks || [];
          break;
        case 'accommodation':
          processedEntity.relatedDays = entity.relatedDays || [];
          break;
        case 'travel':
          processedEntity.segments = entity.segments || [];
          break;
        case 'document':
          // Note: Actual file data should be handled separately for large files
          processedEntity.metadata = entity.metadata || {};
          break;
      }

      // Include timestamps if available
      if (entity.createdAt) processedEntity.createdAt = entity.createdAt;
      if (entity.updatedAt) processedEntity.updatedAt = entity.updatedAt;

      return processedEntity;
    });
  }

  /**
   * Extract relationships between entities
   * @param {Object} rawData - Raw data containing relationship information
   * @returns {Array} Array of relationship objects
   */
  extractRelationships(rawData) {
    const relationships = [];

    // TODO: Implement relationship extraction logic
    // This would analyze the data to identify:
    // - Day -> Accommodation associations
    // - Place -> Day connections
    // - Document -> Entity links
    // - Content block relationships

    // Example relationship structure:
    // {
    //   id: 'rel-001',
    //   sourceType: 'day',
    //   sourceId: 'day-123',
    //   targetType: 'accommodation',
    //   targetId: 'acc-456',
    //   relationshipType: 'contains',
    //   metadata: { ... }
    // }

    return relationships;
  }

  /**
   * Extract metadata for inclusion in export
   * @param {Object} rawData - Raw data
   * @returns {Object} Extracted metadata
   */
  extractMetadata(rawData) {
    return {
      entityCounts: this.calculateEntityCounts(rawData),
      dataIntegrity: this.checkDataIntegrity(rawData),
      exportStats: {
        totalSize: this.calculateSize(rawData),
        entityTypes: Object.keys(rawData).filter(key => Array.isArray(rawData[key]))
      }
    };
  }

  /**
   * Apply formatting options to JSON data
   * @param {Object} jsonData - Structured JSON data
   * @param {Object} options - Formatting options
   * @returns {string} Formatted JSON string
   */
  applyFormatting(jsonData, options) {
    const indent = options.prettyPrint !== false ? 2 : 0;
    return JSON.stringify(jsonData, null, indent);
  }

  /**
   * Compress JSON data if compression is enabled
   * @param {string} jsonString - JSON data as string
   * @returns {string} Compressed data
   */
  async compressData(jsonString) {
    if (!this.compressionEnabled) {
      return jsonString;
    }

    // TODO: Implement compression (gzip, brotli, etc.)
    // For now, return uncompressed data
    return jsonString;
  }

  /**
   * Parse JSON data for import
   * @param {string|Object} jsonData - JSON data to parse
   * @returns {Object} Parsed and validated data structure
   */
  async parse(jsonData) {
    try {
      // Parse JSON if it's a string
      const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;

      // Validate format version
      if (data.export && data.export.version !== this.formatVersion) {
        console.warn(`Format version mismatch: expected ${this.formatVersion}, got ${data.export.version}`);
      }

      // Validate required structure
      this.validateJsonStructure(data);

      // Return structured data for import processing
      return {
        metadata: data.export,
        trip: data.trip,
        entities: data.entities,
        relationships: data.relationships,
        settings: data.settings,
        contentBlocks: data.contentBlocks,
        isValid: true
      };

    } catch (error) {
      throw new Error(`JSON parse failed: ${error.message}`);
    }
  }

  /**
   * Validate JSON structure for import
   * @param {Object} data - Parsed JSON data
   */
  validateJsonStructure(data) {
    const requiredSections = ['export', 'entities'];

    for (const section of requiredSections) {
      if (!data[section]) {
        throw new Error(`Missing required section: ${section}`);
      }
    }

    // Validate entities structure
    if (!data.entities || typeof data.entities !== 'object') {
      throw new Error('Invalid entities structure');
    }
  }

  /**
   * Generate appropriate filename for export
   * @param {Object} metadata - Export metadata
   * @param {string} extension - File extension
   * @returns {string} Generated filename
   */
  generateFilename(metadata, extension) {
    const timestamp = new Date().toISOString().split('T')[0];

    // Use trip name if available, fallback to tripId
    let tripIdentifier = 'unknown-trip';
    if (metadata.tripName) {
      tripIdentifier = metadata.tripName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 30);
    } else if (metadata.tripId) {
      tripIdentifier = `trip-${metadata.tripId.substring(0, 8)}`;
    }

    const scope = Array.isArray(metadata.scope) && metadata.scope.length > 0 && !metadata.scope.includes('complete')
      ? `-${metadata.scope.join('-')}`
      : '';

    return `${tripIdentifier}${scope}-${timestamp}.${extension}`;
  }

  /**
   * Calculate data size
   * @param {any} data - Data to measure
   * @returns {number} Size in bytes
   */
  calculateSize(data) {
    return new Blob([typeof data === 'string' ? data : JSON.stringify(data)]).size;
  }

  /**
   * Calculate entity counts for metadata
   * @param {Object} rawData - Raw data
   * @returns {Object} Entity counts
   */
  calculateEntityCounts(rawData) {
    const counts = {};

    ['days', 'places', 'accommodation', 'travel', 'documents', 'extras'].forEach(entityType => {
      counts[entityType] = Array.isArray(rawData[entityType]) ? rawData[entityType].length : 0;
    });

    return counts;
  }

  /**
   * Get total entity count across all types
   * @param {Object} rawData - Raw data
   * @returns {number} Total entity count
   */
  getTotalEntityCount(rawData) {
    const counts = this.calculateEntityCounts(rawData);
    return Object.values(counts).reduce((total, count) => total + count, 0);
  }

  /**
   * Check data integrity
   * @param {Object} rawData - Raw data to check
   * @returns {Object} Integrity check results
   */
  checkDataIntegrity(rawData) {
    // TODO: Implement data integrity checks
    return {
      hasOrphanedReferences: false,
      missingRequiredFields: [],
      duplicateIds: [],
      integrityScore: 100
    };
  }

  /**
   * Get entity schemas for validation
   * @returns {Object} Schema definitions
   */
  getEntitySchemas() {
    // TODO: Return actual schema definitions
    return {
      day: { version: '1.0.0' },
      place: { version: '1.0.0' },
      accommodation: { version: '1.0.0' },
      travel: { version: '1.0.0' },
      document: { version: '1.0.0' },
      extra: { version: '1.0.0' }
    };
  }
}

// Export handler instance
const jsonFormatHandler = new JsonFormatHandler();

export { jsonFormatHandler, JsonFormatHandler };
export default jsonFormatHandler;